/**
 * AI Extraction Transformers
 *
 * Maps AiExtractedEventData (backend AI response) → EventFormData
 * for direct use with useFormStore.initializeForm().
 */

import type { AiExtractedEventData, EventFormData } from '../types';

const VALID_CONTACT_TYPES = new Set(['email', 'phone', 'website', 'name', 'landline', 'ticket', 'ticket-print-info']);

/**
 * Convert an AiExtractedEventData object (from backend) into an EventFormData
 * that can be passed directly to useFormStore.initializeForm().
 *
 * NOTE: Location coordinates are NOT provided by AI — they will be 0,0.
 * The user must geocode the location in the WhenWhere step.
 */
export function aiExtractedToFormData(event: AiExtractedEventData): EventFormData {
  const mainDesc = event.descriptions.find(d => d.type === 'main') ?? event.descriptions[0];

  const dates = event.dates
    .filter(d => d.startDate)
    .map(d => {
      const start = new Date(d.startDate);
      const end = d.endDate ? new Date(d.endDate) : null;
      const validStart = !isNaN(start.getTime()) ? start : null;
      const validEnd = end && !isNaN(end.getTime()) ? end : null;
      return validStart ? { start: validStart, end: validEnd } : null;
    })
    .filter((d): d is { start: Date; end: Date | null } => d !== null);

  const firstLocation = event.location[0] ?? null;
  const locationName = firstLocation?.name ?? '';
  const address = firstLocation?.address;
  const addressParts = [
    address?.street,
    address?.zip,
    address?.locality,
    address?.country,
  ].filter(Boolean);

  type ContactInfoEntry = NonNullable<EventFormData['contactInfos']>[number];
  const contactInfos: ContactInfoEntry[] = event.contactInfos
    .filter(c => VALID_CONTACT_TYPES.has(c.type) && c.content)
    .map(c => ({
      type: c.type as ContactInfoEntry['type'],
      content: c.content,
    }));

  const organisers: EventFormData['organisers'] = event.organisers
    .filter(o => o.name)
    .slice(0, 1)
    .map(o => ({ name: o.name }));

  return {
    title: event.title ?? '',
    categories: event.categories.map(c => c.slug).filter(Boolean),
    dates,
    location: {
      name: locationName,
      address: addressParts.join(', '),
      coordinates: { lat: 0, lng: 0 },
      addressComponents: address
        ? {
            street: address.street ?? '',
            zip: address.zip ?? '',
            locality: address.locality ?? '',
            country: address.country ?? '',
            region: '',
          }
        : undefined,
    },
    description: mainDesc?.text ?? '',
    contactInfos,
    tags: event.tags ?? [],
    organisers,
    isDraft: true,
  };
}
